import { Dropdown, DropdownIndicator } from 'approot/shared/dropdown/dropdown';
import { Differentiation } from 'approot/shared/differentiation/differentiation.constants';
import {
  BlueSquare,
  GreenCircle,
  PurpleCircle,
} from 'approot/shared/differentiation/differentiation.style';
import {
  getDifferentiationDescriptionLesson,
  getDifferentiationDescriptionAssessment,
  getDifferentiationHeading,
} from 'approot/shared/differentiation/differentiation.utils';
import BlackDiamond from 'approot/shared/differentiation/black-diamond';
import OrangeCross from 'approot/shared/differentiation/orange-cross';
import * as Styled from './activity-differentiation-dropdown.style';
import IconTick from 'approot/shared/icons/icon-tick';
import { useDifferentiationTranslation } from 'approot/shared/differentiation/use-differentiation-translation';

type Props = {
  value: Differentiation;
  onClick: (differentiation: Differentiation) => void;
};

export const ActivityDifferentiationLessonDropdown = ({
  value,
  onClick,
}: Props) => {
  const { unitQuizLabel } = useDifferentiationTranslation();

  const greenCircleText = getDifferentiationDescriptionLesson(
    Differentiation.GreenCircle
  );
  const blueSquareText = getDifferentiationDescriptionLesson(
    Differentiation.BlueSquare
  );
  const blackDiamondText = getDifferentiationDescriptionLesson(
    Differentiation.BlackDiamond
  );
  const orangeCrossText = getDifferentiationDescriptionLesson(
    Differentiation.OrangeCross
  );

  return (
    <Dropdown
      aria-label="Select a differentiation option"
      dropdownContents={
        <>
          <Styled.DifferentiationDropdownItem
            onClick={() => onClick(Differentiation.GreenCircle)}
            data-selector="tls-green-circle"
          >
            <Styled.DifferentiationDropdownItemBox>
              <GreenCircle $width={24} $height={24} />
            </Styled.DifferentiationDropdownItemBox>
            <div>
              <Styled.DifferentiationDropdownItemTitle
                $differentiation={Differentiation.GreenCircle}
              >
                {greenCircleText.title}
              </Styled.DifferentiationDropdownItemTitle>
              <Styled.DifferentiationDropdownItemDescription
                $differentiation={Differentiation.GreenCircle}
              >
                {greenCircleText.description}
              </Styled.DifferentiationDropdownItemDescription>
            </div>
          </Styled.DifferentiationDropdownItem>

          <Styled.DifferentiationDropdownItem
            onClick={() => onClick(Differentiation.BlueSquare)}
            data-selector="tls-blue-square"
          >
            <Styled.DifferentiationDropdownItemBox>
              <BlueSquare $width={24} $height={24} />
            </Styled.DifferentiationDropdownItemBox>
            <div>
              <Styled.DifferentiationDropdownItemTitle
                $differentiation={Differentiation.BlueSquare}
              >
                {blueSquareText.title}
              </Styled.DifferentiationDropdownItemTitle>
              <Styled.DifferentiationDropdownItemDescription
                $differentiation={Differentiation.BlueSquare}
              >
                {blueSquareText.description}
              </Styled.DifferentiationDropdownItemDescription>
            </div>
          </Styled.DifferentiationDropdownItem>

          <Styled.DifferentiationDropdownItem
            onClick={() => onClick(Differentiation.BlackDiamond)}
            data-selector="tls-black-diamond"
          >
            <Styled.DifferentiationDropdownItemBox>
              <BlackDiamond size={22} />
            </Styled.DifferentiationDropdownItemBox>
            <div>
              <Styled.DifferentiationDropdownItemTitle
                $differentiation={Differentiation.BlackDiamond}
              >
                {blackDiamondText.title}
              </Styled.DifferentiationDropdownItemTitle>
              <Styled.DifferentiationDropdownItemDescription
                $differentiation={Differentiation.BlackDiamond}
              >
                {blackDiamondText.description}
              </Styled.DifferentiationDropdownItemDescription>
            </div>
          </Styled.DifferentiationDropdownItem>

          <Styled.DifferentiationDropdownItem
            onClick={() => onClick(Differentiation.OrangeCross)}
            data-selector="tls-support-or-extension"
          >
            <Styled.DifferentiationDropdownItemBox>
              <OrangeCross size={24} />
            </Styled.DifferentiationDropdownItemBox>
            <div>
              <Styled.DifferentiationDropdownItemTitle
                $differentiation={Differentiation.OrangeCross}
              >
                {orangeCrossText.title}
              </Styled.DifferentiationDropdownItemTitle>
              <Styled.DifferentiationDropdownItemDescription
                $differentiation={Differentiation.OrangeCross}
              >
                {orangeCrossText.description}
              </Styled.DifferentiationDropdownItemDescription>
            </div>
          </Styled.DifferentiationDropdownItem>
        </>
      }
      menuWidth={360}
      dropdownButtonContents={
        <Styled.DifferentiationDropdownToggle>
          {getDifferentiationHeading(value, unitQuizLabel)}
          <DropdownIndicator />
        </Styled.DifferentiationDropdownToggle>
      }
      data-selector="tls-differentiation-option-btn"
    />
  );
};

export const ActivityDifferentiationAssessmentDropdown = ({
  value,
  onClick,
}: Props) => {
  const { unitQuizLabel, unitQuizDescription } =
    useDifferentiationTranslation();

  const greenCircleText = getDifferentiationDescriptionAssessment({
    differentiation: Differentiation.GreenCircle,
    unitQuizLabel,
    unitQuizDescription,
  });
  const blueSquareText = getDifferentiationDescriptionAssessment({
    differentiation: Differentiation.BlueSquare,
    unitQuizLabel,
    unitQuizDescription,
  });
  const blackDiamondText = getDifferentiationDescriptionAssessment({
    differentiation: Differentiation.BlackDiamond,
    unitQuizLabel,
    unitQuizDescription,
  });
  const orangeCrossText = getDifferentiationDescriptionLesson(
    Differentiation.OrangeCross
  );
  const selfAssessmentText = getDifferentiationDescriptionAssessment({
    differentiation: Differentiation.SelfAssessment,
    unitQuizLabel,
    unitQuizDescription,
  });
  const anchorMapText = getDifferentiationDescriptionAssessment({
    differentiation: Differentiation.AnchorMap,
    unitQuizLabel,
    unitQuizDescription,
  });
  const unitQuizText = getDifferentiationDescriptionAssessment({
    differentiation: Differentiation.UnitQuiz,
    unitQuizLabel,
    unitQuizDescription,
  });

  return (
    <Dropdown
      aria-label="Select a differentiation option"
      dropdownContents={
        <>
          <Styled.DifferentiationDropdownItem
            onClick={() => onClick(Differentiation.GreenCircle)}
            data-selector="tls-green-circle"
          >
            <Styled.DifferentiationDropdownItemBox>
              <GreenCircle $width={24} $height={24} />
            </Styled.DifferentiationDropdownItemBox>
            <div>
              <Styled.DifferentiationDropdownItemTitle
                $differentiation={Differentiation.GreenCircle}
              >
                {greenCircleText.title}
              </Styled.DifferentiationDropdownItemTitle>
              <Styled.DifferentiationDropdownItemDescription
                $differentiation={Differentiation.GreenCircle}
              >
                {greenCircleText.description}
              </Styled.DifferentiationDropdownItemDescription>
            </div>
          </Styled.DifferentiationDropdownItem>

          <Styled.DifferentiationDropdownItem
            onClick={() => onClick(Differentiation.BlueSquare)}
            data-selector="tls-blue-square"
          >
            <Styled.DifferentiationDropdownItemBox>
              <BlueSquare $width={24} $height={24} />
            </Styled.DifferentiationDropdownItemBox>
            <div>
              <Styled.DifferentiationDropdownItemTitle
                $differentiation={Differentiation.BlueSquare}
              >
                {blueSquareText.title}
              </Styled.DifferentiationDropdownItemTitle>
              <Styled.DifferentiationDropdownItemDescription
                $differentiation={Differentiation.BlueSquare}
              >
                {blueSquareText.description}
              </Styled.DifferentiationDropdownItemDescription>
            </div>
          </Styled.DifferentiationDropdownItem>

          <Styled.DifferentiationDropdownItem
            onClick={() => onClick(Differentiation.BlackDiamond)}
            data-selector="tls-black-diamond"
          >
            <Styled.DifferentiationDropdownItemBox>
              <BlackDiamond size={22} />
            </Styled.DifferentiationDropdownItemBox>
            <div>
              <Styled.DifferentiationDropdownItemTitle
                $differentiation={Differentiation.BlackDiamond}
              >
                {blackDiamondText.title}
              </Styled.DifferentiationDropdownItemTitle>
              <Styled.DifferentiationDropdownItemDescription
                $differentiation={Differentiation.BlackDiamond}
              >
                {blackDiamondText.description}
              </Styled.DifferentiationDropdownItemDescription>
            </div>
          </Styled.DifferentiationDropdownItem>

          <Styled.DifferentiationDropdownItem
            onClick={() => onClick(Differentiation.OrangeCross)}
            data-selector="tls-support-or-extension"
          >
            <Styled.DifferentiationDropdownItemBox>
              <OrangeCross size={24} />
            </Styled.DifferentiationDropdownItemBox>
            <div>
              <Styled.DifferentiationDropdownItemTitle
                $differentiation={Differentiation.OrangeCross}
              >
                {orangeCrossText.title}
              </Styled.DifferentiationDropdownItemTitle>
              <Styled.DifferentiationDropdownItemDescription
                $differentiation={Differentiation.OrangeCross}
              >
                {orangeCrossText.description}
              </Styled.DifferentiationDropdownItemDescription>
            </div>
          </Styled.DifferentiationDropdownItem>

          <Styled.DifferentiationDropdownItem
            onClick={() => onClick(Differentiation.SelfAssessment)}
            data-selector="tls-self-assessment"
          >
            <Styled.DifferentiationDropdownItemBox>
              <PurpleCircle $width={24} $height={24}>
                <IconTick width={11} height={11} />
              </PurpleCircle>
            </Styled.DifferentiationDropdownItemBox>
            <div>
              <Styled.DifferentiationDropdownItemTitle
                $differentiation={Differentiation.SelfAssessment}
              >
                {selfAssessmentText.title}
              </Styled.DifferentiationDropdownItemTitle>
              <Styled.DifferentiationDropdownItemDescription
                $differentiation={Differentiation.SelfAssessment}
              >
                {selfAssessmentText.description}
              </Styled.DifferentiationDropdownItemDescription>
            </div>
          </Styled.DifferentiationDropdownItem>

          <Styled.DifferentiationDropdownItem
            onClick={() => onClick(Differentiation.AnchorMap)}
            data-selector="tls-anchor-map"
          >
            <Styled.DifferentiationDropdownItemBox>
              <PurpleCircle $width={24} $height={24}>
                <IconTick width={11} height={11} />
              </PurpleCircle>
            </Styled.DifferentiationDropdownItemBox>
            <div>
              <Styled.DifferentiationDropdownItemTitle
                $differentiation={Differentiation.AnchorMap}
              >
                {anchorMapText.title}
              </Styled.DifferentiationDropdownItemTitle>
              <Styled.DifferentiationDropdownItemDescription
                $differentiation={Differentiation.AnchorMap}
              >
                {anchorMapText.description}
              </Styled.DifferentiationDropdownItemDescription>
            </div>
          </Styled.DifferentiationDropdownItem>

          <Styled.DifferentiationDropdownItem
            onClick={() => onClick(Differentiation.UnitQuiz)}
            data-selector="tls-unit-quiz"
          >
            <Styled.DifferentiationDropdownItemBox>
              <PurpleCircle $width={24} $height={24}>
                <IconTick width={11} height={11} />
              </PurpleCircle>
            </Styled.DifferentiationDropdownItemBox>
            <div>
              <Styled.DifferentiationDropdownItemTitle
                $differentiation={Differentiation.UnitQuiz}
              >
                {unitQuizText.title}
              </Styled.DifferentiationDropdownItemTitle>
              <Styled.DifferentiationDropdownItemDescription
                $differentiation={Differentiation.UnitQuiz}
              >
                {unitQuizText.description}
              </Styled.DifferentiationDropdownItemDescription>
            </div>
          </Styled.DifferentiationDropdownItem>
        </>
      }
      menuWidth={360}
      dropdownButtonContents={
        <Styled.DifferentiationDropdownToggle>
          {getDifferentiationHeading(value, unitQuizLabel)}
          <DropdownIndicator />
        </Styled.DifferentiationDropdownToggle>
      }
      data-selector="tls-differentiation-option-btn"
    />
  );
};
